import apiFetch from '@scale/frontend-lib/api-fetch';
import { tryParseErrorResponse } from '@scale/frontend-lib/errorMessages';
import HTTPError from '@scale/frontend-lib/httpError';
import type { ImageQueryFilters } from '@scale/mm-shared';
import {
  ImageMetadataResponse,
  ValidatedImagesResponseDto,
  MAXIMUM_MS_THAT_FRONTEND_SHOULD_WAIT_FOR_RESPONSE_FROM_SERVER,
  ValidateImageUrlRequestPayload,
  checkImageOriginalityResponse,
} from '@scale/mm-shared';

/**
 * validate an image URL and returns metadata if valid
 */
export const validateImageUrl = async (
  body: ValidateImageUrlRequestPayload,
): Promise<
  { data: ImageMetadataResponse; rateLimited: false } | { data?: undefined; rateLimited: true }
> => {
  const res = await Promise.race([
    apiFetch<Response>(null, `/internal/multimodal-images/validate_image_url`, {
      method: 'POST',
      body,
      returnRawResponse: true,
      maxRetries: 1,
    }),
    new Promise<Response>((resolve, reject) =>
      setTimeout(() => reject(), MAXIMUM_MS_THAT_FRONTEND_SHOULD_WAIT_FOR_RESPONSE_FROM_SERVER),
    ),
  ]);

  if (res.status === 429) {
    return { rateLimited: true };
  }

  if (res.ok) {
    return { data: await res.json(), rateLimited: false };
  }

  throw new HTTPError(res.statusText, await tryParseErrorResponse(res));
};

export const validatedSerpApiImageSearch = async (
  filters: ImageQueryFilters,
  config: any,
  page: number,
): Promise<ValidatedImagesResponseDto> => {
  const res = await apiFetch<Response>(
    null,
    `/internal/multimodal-images/validated_serp_api_image_search`,
    {
      method: 'POST',
      body: { filters, config, page },
      returnRawResponse: true,
    },
  );

  if (!res.ok) {
    throw new HTTPError(res.statusText, await tryParseErrorResponse(res));
  }

  return await res.json();
};

export const checkImageOriginality = async (
  file: File,
): Promise<{ data: checkImageOriginalityResponse }> => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await apiFetch<Response>(
    null,
    `/internal/multimodal-images/check_image_originality`,
    {
      method: 'POST',
      body: formData,
      returnRawResponse: true,
    },
  );

  if (!res.ok) {
    throw new HTTPError(res.statusText, await tryParseErrorResponse(res));
  }

  return { data: await res.json() };
};
